import DashboardIcon from "@mui/icons-material/Dashboard";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import PersonIcon from "@mui/icons-material/Person";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import SpokeIcon from "@mui/icons-material/Spoke";
import TuneIcon from "@mui/icons-material/Tune";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import NoCrashIcon from "@mui/icons-material/NoCrash";
import AssistantDirectionIcon from "@mui/icons-material/AssistantDirection";
import StayCurrentPortraitIcon from "@mui/icons-material/StayCurrentPortrait";
import Hive from "@mui/icons-material/Hive";
import HandymanIcon from "@mui/icons-material/Handyman";
import ScienceIcon from "@mui/icons-material/Science";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// ==================== NAVIGATION ROUTES ====================

const BASE_NAV_ROUTES = {
  dashboard: {
    title: "dashboard",
    path: "",
    icon: <DashboardIcon />,
  },
  vehicles: {
    title: "vehicles",
    path: "vehiclesmenu",
    icon: <EmojiTransportationIcon />,
    children: [
      {
        title: "vehicles",
        path: "vehicles",
        icon: <DirectionsCarIcon />,
        parentName: "vehicles",
      },
      {
        title: "groups",
        path: "groups",
        icon: <SpokeIcon />,
        parentName: "vehicles",
      },
    ],
  },
  ota: {
    title: "OTA",
    path: "otamenu",
    icon: <CloudUploadRoundedIcon />,
    children: [
      {
        title: "images",
        path: "images",
        icon: <UploadFileIcon />,
        parentName: "OTA",
      },
      {
        title: "releases",
        path: "releases",
        icon: <RssFeedIcon />,
        parentName: "OTA",
      },
      {
        title: "deployments",
        path: "deployments",
        icon: <RocketLaunchIcon />,
        parentName: "OTA",
      },
    ],
  },
  digitalTwin: {
    title: "Digital-Twin",
    path: "simulations",
    icon: <Hive />,
    children: [
      {
        title: "Clusters",
        path: "cluster-simulations",
        icon: <Hive />,
      },
      {
        title: "Truncation Tools",
        path: "tool-simulations",
        icon: <HandymanIcon />,
      },
    ],
  },
  testLab: {
    title: "Test Lab",
    path: "lab-simulations",
    icon: <ScienceIcon />,
    children: [
      {
        title: "Lab Clusters",
        path: "lab-cluster-simulations",
        icon: <Hive />,
      },
      {
        title: "Lab Truncation Tools",
        path: "lab-tool-simulations",
        icon: <HandymanIcon />,
      },
    ],
  },
  customConfigs: {
    title: "custom configs",
    path: "custom-configurations",
    icon: <TuneIcon />,
  },
  users: {
    title: "users",
    path: "users",
    icon: <PersonIcon />,
  },
  reports: {
    title: "reports",
    path: "reports",
    icon: <SummarizeIcon />,
    children: [
      {
        title: "provisioning",
        path: "/reports/provisioning",
        icon: <NoCrashIcon />,
        parentName: "reports",
      },
      {
        title: "scheduler",
        path: "/reports/scheduler",
        icon: <PendingActionsIcon />,
        parentName: "reports",
      },
      {
        title: "navigation",
        path: "/reports/navigation",
        icon: <AssistantDirectionIcon />,
        parentName: "reports",
      },
      {
        title: "deployment",
        path: "/reports/deployment",
        icon: <RocketLaunchIcon />,
        parentName: "reports",
      },
      {
        title: "last seen",
        path: "/reports/lastseen",
        icon: <AccessTimeIcon />,
        parentName: "reports",
      },
    ],
  },
  sdvFeatures: {
    title: "SDV Features",
    path: "sdv-app-features",
    icon: <StayCurrentPortraitIcon />,
  },
};

// ==================== CUSTOMER CONFIGURATIONS ====================

const CUSTOMER_CONFIGS = {
  HD: {
    name: "HD",
    // Navigation routes for HD customer
    navigation: [
      "dashboard",
      "vehicles",
      "ota",
      "digitalTwin",
      "customConfigs",
      "users",
      "reports",
      "sdvFeatures",
    ],
    // Feature flags for HD customer
    features: {
      showHUManufactured: true,
      showFeaturesEnabled: true,
      showVehicleAuditLogs: true,
      showCertificateAuditLogs: true,
      showVehiclePurchaseDate: true,
      showLastSeenAt: true,
    },
    // Custom labels for HD customer
    labels: {
      huManufactured: "HU Manufactured",
      vehicleManufactured: "Vehicle Manufactured",
    },
  },
  MSIL: {
    name: "MSIL",
    navigation: [
      "dashboard",
      "vehicles",
      "ota",
      "testLab",
      "users",
      "sdvFeatures",
    ],
    features: {
      showHUManufactured: false,
      showFeaturesEnabled: false,
      showVehicleAuditLogs: true,
      showCertificateAuditLogs: true,
      showVehiclePurchaseDate: true,
      showLastSeenAt: true,
    },
    labels: {
      huManufactured: "HU Manufactured",
      vehicleManufactured: "Vehicle Manufactured",
    },
  },
  SKODA: {
    name: "SKODA",
    navigation: ["dashboard", "vehicles", "customConfigs", "users", "reports"],
    features: {
      showHUManufactured: false,
      showFeaturesEnabled: true,
      showVehicleAuditLogs: true,
      showCertificateAuditLogs: false,
      showVehiclePurchaseDate: true,
      showLastSeenAt: true,
    },
    labels: {
      huManufactured: "Device Manufactured",
      vehicleManufactured: "Vehicle Manufactured",
    },
  },
};

// ==================== HELPER FUNCTIONS ====================

/**
 * Get the current customer from localStorage
 * @returns {string} Customer code (HD, MSIL, SKODA)
 */
const getCurrentCustomer = () => {
  return localStorage.getItem("selectedCustomer") || "HD";
};

/**
 * Get the complete configuration for current customer
 * @returns {Object} Customer configuration object
 */
export const getCustomerConfig = () => {
  const customer = getCurrentCustomer();
  return CUSTOMER_CONFIGS[customer] || CUSTOMER_CONFIGS.HD;
};

/**
 * Get navigation routes for current customer
 * @returns {Array} Array of navigation route objects
 */
export const getNavRoutes = () => {
  const config = getCustomerConfig();
  return config.navigation.map((key) => BASE_NAV_ROUTES[key]).filter(Boolean);
};

/**
 * Check if a specific feature is enabled for current customer
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} True if feature is enabled
 */
export const isFeatureEnabled = (featureName) => {
  const config = getCustomerConfig();
  return config.features[featureName] ?? false;
};

/**
 * Get customer-specific label
 * @param {string} labelKey - Key of the label
 * @returns {string} Customer-specific label or the key itself
 */
export const getCustomerLabel = (labelKey) => {
  const config = getCustomerConfig();
  return config.labels[labelKey] || labelKey;
};

/**
 * Get customer name
 * @returns {string} Customer name
 */
export const getCustomerName = () => {
  const config = getCustomerConfig();
  return config.name;
};

/**
 * Check if current customer matches the given customer code
 * @param {string} customerCode - Customer code to check (HD, MSIL, SKODA)
 * @returns {boolean} True if matches
 */
export const isCustomer = (customerCode) => {
  return getCurrentCustomer() === customerCode;
};

const CustomerConfigService = {
  getCustomerConfig,
  getNavRoutes,
  isFeatureEnabled,
  getCustomerLabel,
  getCustomerName,
  isCustomer,
  CUSTOMER_CONFIGS,
};

export default CustomerConfigService;
